/**
 * De klok rechtsboven, en de demo-tijd.
 *
 * De demo-tijd bestaat om tijdens een demonstratie of het werkbezoek de
 * NU-markering door de dag te kunnen laten lopen zonder de systeemklok van een
 * tablet te verzetten. Hij verschuift alleen wat de app tóónt; de data uit de
 * backend blijft die van vandaag.
 */
import { useCallback, useEffect, useState } from 'react';

export interface Klok {
  /** Wat het scherm toont: de echte tijd, of de demo-tijd. */
  tijd: Date;
  /**
   * De echte klok, ongeacht de demo-tijd. De voortgangsbalk naar de volgende poll
   * moet hierop rekenen: die gaat over hoe lang het écht nog duurt, en zou met een
   * verschoven demo-tijd anders vol of leeg blijven staan.
   */
  echtNu: number;
}

/** Tikt elke seconde. Eén interval voor de hele app. */
export function useKlok(demoMinuten: number | null): Klok {
  const [klok, setKlok] = useState<Klok>(() => ({
    tijd: berekenTijd(demoMinuten),
    echtNu: Date.now(),
  }));

  useEffect(() => {
    const tik = () => setKlok({ tijd: berekenTijd(demoMinuten), echtNu: Date.now() });
    // Meteen bijstellen, anders blijft de klok tot de volgende tik op de oude
    // demo-tijd staan zodra de gebruiker de schuif verplaatst.
    tik();
    const id = setInterval(tik, 1000);
    return () => clearInterval(id);
  }, [demoMinuten]);

  return klok;
}

/**
 * De echte tijd, of de demo-tijd met de seconden van de echte klok erbij — zo
 * blijft de klok zichtbaar lopen ook als hij op een demo-tijdstip staat.
 */
function berekenTijd(demoMinuten: number | null): Date {
  const nu = new Date();
  if (demoMinuten === null) return nu;

  const d = new Date(nu);
  d.setHours(0, 0, 0, 0);
  d.setMinutes(demoMinuten);
  d.setSeconds(nu.getSeconds());
  return d;
}

/** Beheert de demo-tijd: null = de echte klok. */
export function useDemoTijd() {
  const [demoMinuten, setDemoMinuten] = useState<number | null>(null);

  const zet = useCallback((minuten: number) => setDemoMinuten(minuten), []);
  const herstel = useCallback(() => setDemoMinuten(null), []);

  return { demoMinuten, zet, herstel };
}
